import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    CustomInput,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Label,
    Input,
    Table,
    Pagination,
    PaginationItem,
    PaginationLink,
    UncontrolledButtonDropdown,
    Badge,
    InputGroup,
    InputGroupAddon,
    UncontrolledTooltip,
    Popover,
    PopoverBody,
} from 'reactstrap';
import { AvForm, AvField, AvGroup, AvInput, AvFeedback } from 'availity-reactstrap-validation';
import { UserContext } from '../contexts/UserContext';
import { SelectedFacilitiesContext } from '../contexts/SelectedFacilitiesContext';
import { Icon } from '@iconify/react';
import dotsHorizontal from '@iconify/icons-mdi/dots-horizontal';
import roundDeleteForever from '@iconify/icons-ic/round-delete-forever';
import plusIcon from '@iconify/icons-mdi/plus';
import accountEye from '@iconify/icons-mdi/account-eye';
import shieldAccount from '@iconify/icons-mdi/shield-account';
import accountIcon from '@iconify/icons-mdi/account';
import Select, { components } from 'react-select';
import arrowsVertical from '@iconify/icons-carbon/arrows-vertical';
import mdiAccountCogOutline from '@iconify/icons-mdi/account-cog-outline';
import arrowUp from '@iconify/icons-carbon/arrow-up';
import arrowDown from '@iconify/icons-carbon/arrow-down';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { SrxApi } from '../api/SrxApi';
import accountDetails from '@iconify/icons-mdi/account-details';
import officeBuildingOutline from '@iconify/icons-mdi/office-building-outline';
import cloneDeep from 'lodash.clonedeep';
import { confirm } from '../utility-components/ConfirmModal';
import deleteIcon from '@iconify/icons-mdi/delete';
import closeIcon from '@iconify/icons-mdi/close';
import lockReset from '@iconify/icons-mdi/lock-reset';
import accountEditOutline from '@iconify/icons-mdi/account-edit-outline';
import { UserRolesContext } from '../contexts/UserRolesContext';
import MaskedInput from 'react-text-mask';
// import Settings from '../UserSettings'
import Settings from '../custom-components/UserSettings';

const USER_ROLE_PERMISSION_KEY = {
    CustomerService: { add: 'canAddUserCustomerService', edit: 'canEditUserCustomerService', delete: 'canDeleteUserCustomerService' },
    FacilityAdmin: { add: 'canAddUserFacilityAdmin', edit: 'canEditUserFacilityAdmin', delete: 'canDeleteUserFacilityAdmin' },
    Manager: { add: 'canAddUserManager', edit: 'canEditUserManager', delete: 'canDeleteUserManager' },
    Staff: { add: 'canAddUserStaff', edit: 'canEditUserStaff', delete: 'canDeleteUserStaff' },
    SysAdmin: { add: 'canAddUserSysAdmin', edit: 'canEditUserSysAdmin', delete: 'canDeleteUserSysAdmin' },
    User: { add: 'canAddUserUser', edit: 'canEditUserUser', delete: 'canDeleteUserUser' },
    ViewOnly: { add: 'canAddUserViewOnly', edit: 'canEditUserViewOnly', delete: 'canDeleteUserViewOnly' },
    Pharmacist: { add: 'canAddUserPharmacist', edit: 'canEditUserPharmacist', delete: 'canDeleteUserPharmacist' },
};

const SearchInput = React.memo(({ searchTerm, setSearchTerm, handleShowAll }) => {
    const [localTerm, setLocalTerm] = useState(searchTerm || '');
    const debounceRef = useRef(null);

    useEffect(() => {
        setLocalTerm(searchTerm || '');
    }, [searchTerm]);

    const handleChange = (e) => {
        const value = e.target.value;
        setLocalTerm(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchTerm(value);
        }, 300);
    };

    return (
        <FormGroup className="d-flex mb-0 align-items-center" style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
            <Label className="mr-2 mb-0">
                <b>Search:</b>
            </Label>
            <Input value={localTerm} onChange={handleChange} placeholder="Type at least 3 characters..." />
            <Button
                color="secondary"
                outline
                className="ml-2"
                style={{ whiteSpace: 'nowrap', border: '1px solid #c8ccd1', cursor: 'pointer' }}
                onClick={() => {
                    setLocalTerm('');
                    handleShowAll();
                }}>
                Show All
            </Button>
        </FormGroup>
    );
});

// Used to request the whole matching set in one call (search / facility-filter results are shown unpaginated)
const ALL_RESULTS_PAGE_SIZE = 100000;

const Users = () => {
    const { faciToken, setFaciToken, user } = useContext(UserContext);

    // undefined = idle (no search done yet), null = loading, array = results
    const [users, setUsers] = useState(undefined);
    const [showingAll, setShowingAll] = useState(false);
    const [deletedUser, setDeletedUser] = useState(null);

    const [entityId, setEntityId] = useState('');
    const [UserId, setUserId] = useState('');

    const [searchTerm, setSearchTerm] = useState('');

    const [userFormModal, setUserFormModal] = useState({
        isOpen: false,
        mode: 'add',
        user: null,
        readOnly: false,
    });
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 50,
        totalPages: 0,
        totalCount: 0,
    });

    const { facilities, selectedFacilities } = useContext(SelectedFacilitiesContext);
    const [numberFacilities, setNumberFacilities] = useState(user.numberFacilities);

    const facilityIds = (selectedFacilities || []).map((f) => f?.facID).filter((id) => id != null && id !== '');

    // Stable key so the effect below only re-runs when the actual facility selection changes, not just its length
    const facilityIdsKey = facilityIds.slice().sort().join(',');
    const isSearchActive = searchTerm.trim().length >= 3;
    const hasFacilityFilter = facilityIds.length > 0;
    // Search / facility-filter results are shown in full, with no pagination
    const isFiltered = isSearchActive || hasFacilityFilter;

    const [resetPasswordModal, setResetPasswordModal] = useState({
        isOpen: false,
        data: '',
    });

    function closePasswordModal() {
        setResetPasswordModal({
            isOpen: false,
            data: '',
        });
    }

    function closeUserFormModal() {
        setUserFormModal({
            isOpen: false,
            mode: 'add',
            user: null,
            readOnly: false,
        });
    }

    const [editUserSettingsModal, setEditUserSettingsModal] = useState({
        isOpen: false,
        data: {
            user: null,
        },
    });

    function closeEditUserSettingsModal() {
        setEditUserSettingsModal({
            isOpen: false,
            data: {
                user: null,
            },
        });
    }

    const [deleteUserModal, setDeleteUserModal] = useState({
        isOpen: false,
        data: '',
    });

    function closeDeleteUserModal() {
        setDeleteUserModal({
            isOpen: false,
            data: '',
        });
    }

    // const undoDelete = () => {
    //     console.log(deletedUser)
    //     if (deletedUser) {
    //         // const payload = {
    //         //     ...deletedUser,
    //         //     faciID: deletedUser.entityTypeAndRole[0].facid,
    //         //     email: deletedUser.email,
    //         //     entities: JSON.stringify(deletedUser.entityTypeAndRole[0].facid),
    //         //     userRole: deletedUser.entityTypeAndRole[0].userRole,
    //         //     UserRole: deletedUser.entityTypeAndRole[0].userRole,
    //         //     UserType: 'FacilityUser',
    //         //     title: deletedUser.title,
    //         //     UserId: deletedUser.id,
    //         //     model: deletedUser.id,
    //         //     password: deletedUser.mediprocityInfo.password,

    //         // };

    //         // SrxApi.post('/createuser', payload)
    //         //     .then((res) => {
    //         //         console.log(res.data);
    //         //         toast.success(`${deletedUser.displayName || deletedUser.email} added successfully`);
    //         //     })
    //         //     .catch((err) => {
    //         //         toast.error('Error adding user: ' + err.message);
    //         //     });
    //         //update users list
    //         const updatedUsers = [...users, deletedUser];
    //         console.log(users)
    //         console.log(updatedUsers)
    //         setUsers(updatedUsers);
    //         setDeletedUser(null);
    //     }
    // }

    // useEffect(() => {
    //     if (user?.userType !== 'FacilityUser') {
    //         history.replace('/dashboard')
    //     }
    // }, [user])

    let history = useHistory();

    // Track latest active search term to prevent out-of-order API responses
    const activeSearchTermRef = useRef('');

    // Fetch users — pass a searchTerm to filter server-side, or omit for all users
    // function getFacilityUsers(term) {
    //     const currentTerm = term || '';
    //     activeSearchTermRef.current = currentTerm;
    //     setUsers(null); // null = loading
    //     const params = term ? { search: term } : {};
    //     SrxApi.get('/getusers', { params })
    //         .then(res => {
    //             if (activeSearchTermRef.current === currentTerm) {
    //                 setUsers(res.data.users);
    //             }
    //         })
    //         .catch(err => {
    //             if (activeSearchTermRef.current === currentTerm) {
    //                 setUsers([]);
    //                 toast.error('Error retrieving facility users');
    //                 if (err.response?.status === 401) {
    //                     localStorage.removeItem('FACI_TOKEN');
    //                     setFaciToken(null);
    //                     history.replace('/account/login');
    //                 }
    //             }
    //         });
    // }

    function getFacilityUsers(term = '', page = pagination.pageNumber, pageSizeOverride = null) {
        const currentTerm = term.trim();
        activeSearchTermRef.current = currentTerm;

        setUsers(null);

        SrxApi.get('/getusers', {
            params: {
                search: currentTerm || undefined,
                pageNumber: page,
                pageSize: pageSizeOverride || pagination.pageSize,
            },
        })
            .then((res) => {
                if (activeSearchTermRef.current !== currentTerm) return;

                setUsers(res.data.items);

                setPagination((prev) => ({
                    ...prev,
                    pageNumber: res.data.pageNumber,
                    totalPages: res.data.totalPages,
                    totalCount: res.data.totalCount,
                }));
            })
            .catch((err) => {
                setUsers([]);
                toast.error('Error retrieving facility users');
            });
    }

    const handlePageChange = (page) => {
        setPagination((prev) => ({
            ...prev,
            pageNumber: page,
        }));

        getFacilityUsers('', page);
    };
    function handleShowAll() {
        setSearchTerm('');
        setShowingAll(true);

        setPagination((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
    }

    const hasMountedRef = useRef(false);

    const skipNextAutoFetchRef = useRef(false);

    useEffect(() => {
        const isFirstRun = !hasMountedRef.current;
        hasMountedRef.current = true;

        if (isFirstRun && !isSearchActive && !hasFacilityFilter) {
            return;
        }

        if (!isFirstRun && skipNextAutoFetchRef.current) {
            skipNextAutoFetchRef.current = false;
            return;
        }

        if (isSearchActive || hasFacilityFilter) {
            setPagination((prev) => ({
                ...prev,
                pageNumber: 1,
            }));

            getFacilityUsers(isSearchActive ? searchTerm.trim() : '', 1, ALL_RESULTS_PAGE_SIZE);
            return;
        }

        if (showingAll) {
            getFacilityUsers('', 1);
            return;
        }

        activeSearchTermRef.current = '';
        setUsers(undefined);
    }, [searchTerm, facilityIdsKey, showingAll]);

    const filteredUsers = React.useMemo(() => {
        if (!Array.isArray(users)) return users;
        if (facilityIds.length === 0) return users;
        return users.filter((u) => (u.entityTypeAndRole || []).some((role) => facilityIds.includes(role.facid)));
    }, [users, facilityIds]);

    const searchUsers = React.useCallback((array, termToSearch) => {
        if (!Array.isArray(array)) return array;
        const term = (termToSearch || '').toLowerCase().trim();
        if (!term) return array;

        return array.filter((u) => {
            if (!u) return false;

            if (u.displayName && String(u.displayName).toLowerCase().includes(term)) return true;
            if (u.email && String(u.email).toLowerCase().includes(term)) return true;
            if (u.phone && String(u.phone).toLowerCase().includes(term)) return true;
            if (u.title && String(u.title).toLowerCase().includes(term)) return true;

            if (Array.isArray(u.entityTypeAndRole)) {
                const matchRoleOrFac = u.entityTypeAndRole.some((roleObj) => {
                    if (!roleObj) return false;
                    const roleName = roleObj.userRole || roleObj.role || '';
                    const facName = roleObj.facilityName || roleObj.facName || roleObj.facid || '';
                    return (
                        String(roleName).toLowerCase().includes(term) || String(facName).toLowerCase().includes(term)
                    );
                });
                if (matchRoleOrFac) return true;
            }

            if (Array.isArray(u.facilities)) {
                const matchFac = u.facilities.some((fac) => {
                    if (!fac) return false;
                    const facString = typeof fac === 'string' ? fac : fac.facilityName || fac.name || fac.facID || '';
                    return String(facString).toLowerCase().includes(term);
                });
                if (matchFac) return true;
            }

            return false;
        });
    }, []);

    const [sortBy, setSortBy] = useState({
        sortField: 'displayName',
        sortDirection: 'asc',
    });

    const sortByField = React.useCallback(
        (array) => {
            if (!Array.isArray(array)) return array;
            const arr = [...array];
            switch (sortBy.sortField) {
                case 'email':
                    if (sortBy.sortDirection === 'asc')
                        return arr.sort((a, b) => ((a?.email || '') < (b?.email || '') ? -1 : 1));
                    else return arr.sort((a, b) => ((a?.email || '') > (b?.email || '') ? -1 : 1));
                case 'displayName':
                    if (sortBy.sortDirection === 'asc')
                        return arr.sort((a, b) => ((a?.displayName || '') < (b?.displayName || '') ? -1 : 1));
                    else return arr.sort((a, b) => ((a?.displayName || '') > (b?.displayName || '') ? -1 : 1));
                default:
                    return arr;
            }
        },
        [sortBy]
    );

    const displayUsers = React.useMemo(() => {
        if (!Array.isArray(filteredUsers)) return filteredUsers;
        const searched = searchUsers(filteredUsers, searchTerm);
        return sortByField(searched);
    }, [filteredUsers, searchTerm, searchUsers, sortByField]);

    function onClickSort(field) {
        if (field === sortBy.sortField) {
            setSortBy({
                ...sortBy,
                sortDirection: sortBy.sortDirection === 'asc' ? 'desc' : 'asc',
            });
        } else {
            setSortBy({
                sortField: field,
                sortDirection: 'asc',
            });
        }
    }

    const SortCarets = () => (
        <>
            {sortBy.sortDirection === 'asc' ? (
                <Icon icon={arrowUp} color="black" />
            ) : (
                <Icon icon={arrowDown} color="black" />
            )}
        </>
    );

    function formatPhoneNumber(phoneNumberString) {
        var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
        var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            var intlCode = match[1] ? '+1 ' : '';
            return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
        }
        return null;
    }

    const canChangeUserSettings = (user_) => {
        let thePermission = 'canSetSettingsForUsers';

        let selectedUserRoles = user_?.entityTypeAndRole || [];
        let signedInUserFacID = facilities?.map((f) => f.facID);
        // convert signedInUserFacID into an array of objects
        signedInUserFacID = signedInUserFacID.map((facid) => {
            return {
                facid,
            };
        });
        function findMatchingFacid(signedInUserFacID, selectedUserRoles) {
            let matchingFacid = [];
            // Loop through the facID array of the signed in user
            for (const role1 of signedInUserFacID) {
                const facid1 = role1.facid;

                for (const role2 of selectedUserRoles) {
                    const facid2 = role2.facid;

                    if (facid1 === facid2) {
                        matchingFacid.push(facid1);
                    }
                }
            }

            return matchingFacid;
        }
        let hasMatchingFacid = findMatchingFacid(signedInUserFacID, selectedUserRoles);
        if (hasMatchingFacid.length === 0) {
            //console.log("No matching facid found.");
            return false;
        }

        let matchingFacility = facilities?.filter((facility) => hasMatchingFacid.includes(facility.facID));
        return matchingFacility[0]?.permissions?.[thePermission];
    };

    const canResetPassword = (user_) => {
        let selectedUserRoles = user_?.entityTypeAndRole || [];
        let signedInUserFacID = facilities?.map((f) => f.facID);
        // convert signedInUserFacID into an array of objects
        signedInUserFacID = signedInUserFacID.map((facid) => {
            return {
                facid,
            };
        });
        function findMatchingFacid(signedInUserFacID, selectedUserRoles) {
            let matchingFacid = [];
            // Loop through the facID array of the signed in user
            for (const role1 of signedInUserFacID) {
                const facid1 = role1.facid;

                for (const role2 of selectedUserRoles) {
                    const facid2 = role2.facid;

                    if (facid1 === facid2) {
                        matchingFacid.push(facid1);
                    }
                }
            }

            return matchingFacid;
        }
        let hasMatchingFacid = findMatchingFacid(signedInUserFacID, selectedUserRoles);
        if (hasMatchingFacid.length === 0) {
            //console.log("No matching facid found.");
            return false;
        }

        let matchingFacility = facilities?.filter((facility) => hasMatchingFacid.includes(facility.facID));
        let selectedUserRole = [...new Set(user_?.entityTypeAndRole?.map((r) => r.userRole))];
        let permissionRole = selectedUserRole.map((role) => USER_ROLE_PERMISSION_KEY[role]?.edit).filter(Boolean);
        let allUserPermissions = matchingFacility.map((facility) => facility.permissions);
        // Allowed if, on any shared facility, the signed-in user is actually granted edit on any of the selected user's roles.
        return permissionRole.some((key) => allUserPermissions.some((permissions) => permissions?.[key] === true));
    };

    const canAddFacility = (user_) => {
        let selectedUserRoles = user_?.entityTypeAndRole || [];
        let signedInUserFacID = facilities?.map((f) => f.facID);
        signedInUserFacID = signedInUserFacID.map((facid) => ({ facid }));

        function findMatchingFacid(signedInUserFacID, selectedUserRoles) {
            let matchingFacid = [];
            for (const role1 of signedInUserFacID) {
                const facid1 = role1.facid;
                for (const role2 of selectedUserRoles) {
                    const facid2 = role2.facid;
                    if (facid1 === facid2) {
                        matchingFacid.push(facid1);
                    }
                }
            }
            return matchingFacid;
        }

        let hasMatchingFacid = findMatchingFacid(signedInUserFacID, selectedUserRoles);
        if (hasMatchingFacid.length === 0) return false;

        let matchingFacility = facilities?.filter((facility) => hasMatchingFacid.includes(facility.facID));
        let allUserPermissions = matchingFacility.map((facility) => facility.permissions);
        return allUserPermissions.some((permissions) => permissions?.canAddFacilityToUser === true);
    };

    const canDelete = (user_) => {
        let selectedUserRoles = user_?.entityTypeAndRole || []; //returns entityTypeAndRole array/s of selected user
        let signedInUserFacID = facilities?.map((f) => f.facID); // returns array of facID of signed in user
        // convert signedInUserFacID into an array of objects
        signedInUserFacID = signedInUserFacID.map((facid) => {
            return {
                facid,
            };
        });
        function findMatchingFacid(signedInUserFacID, selectedUserRoles) {
            let matchingFacid = [];
            // Loop through the facID array of the signed in user
            for (const role1 of signedInUserFacID) {
                const facid1 = role1.facid;

                for (const role2 of selectedUserRoles) {
                    const facid2 = role2.facid;

                    if (facid1 === facid2) {
                        matchingFacid.push(facid1);
                    }
                }
            }

            return matchingFacid;
        }
        let hasMatchingFacid = findMatchingFacid(signedInUserFacID, selectedUserRoles); // returns common facID of both users
        if (hasMatchingFacid.length === 0) {
            //console.log("No matching facid found.");
            return false;
        }

        let matchingFacility = facilities?.filter((facility) => hasMatchingFacid.includes(facility.facID)); //returns common facilties!
        let selectedUserRole = [...new Set(user_?.entityTypeAndRole?.map((r) => r.userRole))]; //returns selected user role/s without duplicates
        let permissionRole = selectedUserRole.map((role) => USER_ROLE_PERMISSION_KEY[role]?.delete).filter(Boolean);
        let allUserPermissions = matchingFacility.map((facility) => facility.permissions); //returns all user permissions
        // Allowed if, on any shared facility, the signed-in user is actually granted delete on any of the selected user's roles.
        return permissionRole.some((key) => allUserPermissions.some((permissions) => permissions?.[key] === true));
    };

    const getPageNumbers = () => {
        const current = pagination.pageNumber;
        const total = pagination.totalPages;

        let pages = [];

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            if (current <= 4) {
                pages = [1, 2, 3, 4, 5, '...', total];
            } else if (current >= total - 3) {
                pages = [1, '...', total - 4, total - 3, total - 2, total - 1, total];
            } else {
                pages = [1, '...', current - 1, current, current + 1, '...', total];
            }
        }

        return pages;
    };

    return (
        <>
            <div className="mt-3 mx-2">
                <h2>Facility Users</h2>
                <Card>
                    <CardBody>
                        {/* <h4 className="header-title">Product List</h4>
                <p className="text-muted font-14 mb-4">A list of all existing Greenbowl Products</p> */}
                        <Row className="mb-2 d-flex justify-content-between">
                            <Col xs={'auto'}>
                                <Button
                                    color="primary"
                                    onClick={() => {
                                        setUserFormModal({ isOpen: true, mode: 'add', user: null });
                                    }}>
                                    <i className="mdi mdi-plus-circle mr-2"></i>Add Facility User
                                </Button>
                            </Col>
                            <Col className="text-right" md={6}>
                                <SearchInput
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    handleShowAll={handleShowAll}
                                />
                            </Col>
                        </Row>
                        <Table className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th onClick={() => onClickSort('displayName')} className="th-hover">
                                        Name{' '}
                                        {sortBy.sortField === 'displayName' ? (
                                            <SortCarets />
                                        ) : (
                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                        )}
                                    </th>
                                    <th onClick={() => onClickSort('email')} className="th-hover">
                                        Email{' '}
                                        {sortBy.sortField === 'email' ? (
                                            <SortCarets />
                                        ) : (
                                            <Icon icon={arrowsVertical} style={{ opacity: '0.6' }} />
                                        )}
                                    </th>
                                    <th>Phone</th>
                                    <th>Facilities</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users === undefined ? (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted py-4">
                                            <i className="mdi mdi-magnify mr-1" />
                                            Type at least 3 characters to search for users, or click{' '}
                                            <b onClick={handleShowAll} style={{ cursor: 'pointer' }}>
                                                Show All
                                            </b>
                                            .
                                        </td>
                                    </tr>
                                ) : users === null ? (
                                    <tr>
                                        <td colSpan={4} className="text-center py-4">
                                            <div className="bouncing-loader d-inline-flex">
                                                <div></div>
                                                <div></div>
                                                <div></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : Array.isArray(displayUsers) && displayUsers.length > 0 ? (
                                    displayUsers.map((user_, index) => {
                                        return (
                                            <tr key={index}>
                                                <th scope="row" style={{ verticalAlign: 'middle' }}>
                                                    {user_.displayName}
                                                    {user_.email === user.email && (
                                                        <Badge
                                                            color="dark"
                                                            className="ml-1"
                                                            style={{ fontSize: '12px' }}>
                                                            Self
                                                        </Badge>
                                                    )}
                                                </th>
                                                <td style={{ verticalAlign: 'middle' }}>{user_.email}</td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {formatPhoneNumber(user_.phone)}
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }} className="position-relative">
                                                    <b>{user_.numberFacilities}</b>
                                                    <UncontrolledButtonDropdown
                                                        className="position-absolute"
                                                        style={{ right: '20px', top: '5px' }}>
                                                        <DropdownToggle
                                                            tag="button"
                                                            className="btn btn-lg btn-link no-arrow card-drop p-0">
                                                            <Icon icon={dotsHorizontal} width="23" height="23" />
                                                        </DropdownToggle>
                                                        <DropdownMenu right style={{ minWidth: 'max-content' }}>
                                                            {(() => {
                                                                const canEditUser =
                                                                    canResetPassword(user_) || canAddFacility(user_);
                                                                return (
                                                                    <DropdownItem
                                                                        className="px-3 py-1"
                                                                        onClick={() =>
                                                                            setUserFormModal({
                                                                                isOpen: true,
                                                                                mode: 'update',
                                                                                user: user_,
                                                                                readOnly: !canEditUser,
                                                                            })
                                                                        }>
                                                                        <Icon
                                                                            icon={
                                                                                canEditUser
                                                                                    ? accountEditOutline
                                                                                    : accountEye
                                                                            }
                                                                            width="20"
                                                                            height="20"
                                                                            className="mr-1"
                                                                        />
                                                                        {canEditUser ? 'Update User' : 'View Facilities'}
                                                                    </DropdownItem>
                                                                );
                                                            })()}
                                                            {canResetPassword(user_) && (
                                                                <DropdownItem
                                                                    className="px-3 py-1"
                                                                    disabled={!user_.requestorIsAdmin}
                                                                    onClick={() =>
                                                                        setResetPasswordModal({
                                                                            isOpen: true,
                                                                            data: user_,
                                                                        })
                                                                    }>
                                                                    <Icon
                                                                        icon={lockReset}
                                                                        width="20"
                                                                        height="20"
                                                                        className="mr-1"
                                                                    />
                                                                    Reset Password
                                                                </DropdownItem>
                                                            )}
                                                            {canChangeUserSettings(user_) && (
                                                                <DropdownItem
                                                                    className="px-3 py-1"
                                                                    onClick={() =>
                                                                        setEditUserSettingsModal({
                                                                            isOpen: true,
                                                                            data: user_,
                                                                        })
                                                                    }>
                                                                    <Icon
                                                                        icon={mdiAccountCogOutline}
                                                                        width="20"
                                                                        height="20"
                                                                        className="mr-1"
                                                                    />
                                                                    Edit User Settings
                                                                </DropdownItem>
                                                            )}
                                                            {canDelete(user_) && (
                                                                <DropdownItem
                                                                    className="px-3 py-1"
                                                                    onClick={() =>
                                                                        setDeleteUserModal({
                                                                            isOpen: true,
                                                                            data: user_,
                                                                        })
                                                                    }>
                                                                    <Icon
                                                                        icon={roundDeleteForever}
                                                                        width="20"
                                                                        height="20"
                                                                        className="mr-1"
                                                                    />
                                                                    Delete User
                                                                </DropdownItem>
                                                            )}
                                                        </DropdownMenu>
                                                    </UncontrolledButtonDropdown>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center">
                                            <b>No Users Found</b>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {!isFiltered && Array.isArray(users) && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <p className="text-muted size-sm">
                            Showing page <b>{pagination.pageNumber}</b> of <b>{pagination.totalPages}</b> (
                            {pagination.totalCount} users)
                        </p>

                        <Pagination size="md" className="mb-0">
                            <PaginationItem disabled={pagination.pageNumber === 1 || pagination.totalPages <= 1}>
                                <PaginationLink first onClick={() => handlePageChange(1)} />
                            </PaginationItem>

                            <PaginationItem disabled={pagination.pageNumber === 1 || pagination.totalPages <= 1}>
                                <PaginationLink previous onClick={() => handlePageChange(pagination.pageNumber - 1)} />
                            </PaginationItem>

                            {getPageNumbers().map((page, index) =>
                                page === '...' ? (
                                    <PaginationItem disabled key={`ellipsis-${index}`}>
                                        <PaginationLink>...</PaginationLink>
                                    </PaginationItem>
                                ) : (
                                    <PaginationItem key={page} active={pagination.pageNumber === page}>
                                        <PaginationLink onClick={() => handlePageChange(page)}>{page}</PaginationLink>
                                    </PaginationItem>
                                )
                            )}

                            <PaginationItem
                                disabled={
                                    pagination.pageNumber === pagination.totalPages || pagination.totalPages <= 1
                                }>
                                <PaginationLink next onClick={() => handlePageChange(pagination.pageNumber + 1)} />
                            </PaginationItem>

                            <PaginationItem
                                disabled={
                                    pagination.pageNumber === pagination.totalPages || pagination.totalPages <= 1
                                }>
                                <PaginationLink last onClick={() => handlePageChange(pagination.totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
                {/* {deletedUser && (
                    <div className="mt-2">
                        <p>{deletedUser.displayName || deletedUser.email} has been deleted. <b>Undo the deletion?</b></p>
                        <Button color="secondary" onClick={undoDelete}>Undo</Button>
                    </div>
                    )} */}
                <UserFormModal
                    isOpen={userFormModal.isOpen}
                    mode={userFormModal.mode}
                    user={userFormModal.user}
                    readOnly={userFormModal.readOnly}
                    toggle={closeUserFormModal}
                    setFaciToken={setFaciToken}
                    users={users || []}
                    setUsers={setUsers}
                    contextUser={user}
                    onUserCreated={(newUser) => {
                        skipNextAutoFetchRef.current = true;
                        setTimeout(() => {
                            skipNextAutoFetchRef.current = false;
                        }, 0);
                        setSearchTerm('');
                        setShowingAll(false);
                        setUsers((prevUsers) => (Array.isArray(prevUsers) ? [...prevUsers, newUser] : [newUser]));
                    }}
                />
                <ResetPasswordModal
                    isOpen={resetPasswordModal.isOpen}
                    toggle={closePasswordModal}
                    user={resetPasswordModal.data}
                    entityId={entityId}
                />
                <EditUserSettingsModal
                    isOpen={editUserSettingsModal.isOpen}
                    toggle={closeEditUserSettingsModal}
                    user={editUserSettingsModal.data}
                    entityId={entityId}
                    setFaciToken={setFaciToken}
                    users={users || []}
                    setUsers={setUsers}
                    contextUser={user}
                />
                <DeleteUserModal
                    isOpen={deleteUserModal.isOpen}
                    toggle={closeDeleteUserModal}
                    user={deleteUserModal.data}
                    entityId={entityId}
                    users={users || []}
                    setUsers={setUsers}
                    contextUser={user}
                    UserId={UserId}
                    deleteUserModal={deleteUserModal}
                    setDeleteUserModal={setDeleteUserModal}
                    closeDeleteUserModal={closeDeleteUserModal}
                    deletedUser={deletedUser}
                    setDeletedUser={setDeletedUser}
                />
            </div>
        </>
    );
};

const DeleteUserModal = ({
    isOpen,
    toggle,
    user,
    entityId,
    users,
    setUsers,
    contextUser,
    UserId,
    deletedUser,
    setDeletedUser,
    setDeleteUserModal,
}) => {
    const deleteUser = (deletedUser) => {
        const payload = {
            email: deletedUser.email,
            entityId: deletedUser.entityTypeAndRole[0].facid,
            UserId: deletedUser.id,
        };

        SrxApi.post('/deleteuser', payload)
            .then((res) => {
                const updatedUsers = users.filter((user) => user.email !== deletedUser.email);
                setUsers(updatedUsers);
                setDeletedUser(deletedUser); // Store the deleted user for potential undo
                setDeleteUserModal({ isOpen: false, data: '' });
                // setDeletedUser(null);
                toggle();
                console.log(deletedUser);
                toast.success(`${deletedUser.displayName || deletedUser.email} deleted successfully`);
            })
            .catch((err) => {
                toast.error('Error deleting user: ' + err.message);
            });
    };

    const [userFacilities, setUserFacilities] = useState(null);
    useEffect(() => {
        if (isOpen) {
            setUserFacilities([]);
            SrxApi.get('/userfacilities', {
                params: {
                    email: user.email,
                },
            })
                .then(({ data }) => setUserFacilities(data.facilities))
                .catch((err) => {
                    toast.error('Error: ' + err.message);
                });
        }
    }, [isOpen, user?.email]);

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle} className="modal-colored-header bg-primary">
                Delete User
            </ModalHeader>
            <ModalBody>
                <h5>
                    Are you sure you want to delete <b>{user.displayName}</b>?
                </h5>
                
                {userFacilities && userFacilities?.length > 0 && (
                    <div
                        style={{
                            maxHeight: '320px',
                            overflowY: 'auto',
                            border: '1px solid #e9ecef',
                            borderRadius: '4px',
                        }}>
                        <table className="mb-0" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>Facilities</th>
                                </tr>
                            </thead>
                            <tbody>
                                {userFacilities
                                    .sort((a, b) => (a.facName > b.facName ? 1 : -1))
                                    .map((facility, index) => (
                                        <tr key={index}>
                                            <td style={{ verticalAlign: 'middle', fontWeight: 'bold' }}>
                                                {facility.facID}
                                            </td>
                                            <td style={{ verticalAlign: 'middle', fontWeight: 'normal' }}>
                                                {facility.facName}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {userFacilities?.length > 0 && (
                    <div
                        className="mb-2 p-2"
                        style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '4px', color: '#856404',marginTop: '20px' }}>
                        Deleting this user will also remove them from these facilities.
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="danger" onClick={() => deleteUser(user)}>
                    Delete
                </Button>
                <Button color="secondary" onClick={toggle}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const EditUserSettingsModal = ({ isOpen, toggle, user, users, setUsers, contextUser }) => {
    const handleSave = () => {
        console.log('saving...');
    };
    return (
        <Modal isOpen={isOpen} toggle={toggle} style={{ minWidth: '800px' }}>
            <ModalHeader toggle={toggle} className="modal-colored-header bg-primary">
                Edit settings for <i>"{user.displayName}"</i>
            </ModalHeader>
            <ModalBody>
                <Settings userData={user} toggle={toggle} users={users} setUsers={setUsers} source="facility" />
            </ModalBody>
            {/* <ModalFooter>
                <Button color="primary" onClick={handleSave}>
                    Save
                </Button>
                <Button color="secondary" onClick={toggle}>
                    Cancel
                </Button>
            </ModalFooter> */}
        </Modal>
    );
};

export const createLimitedMultiValueContainer = (maxVisible, idPrefix) => (props) => {
    const [morePopoverOpen, setMorePopoverOpen] = useState(false);
    const { children, getValue, selectProps } = props;
    const selected = getValue() || [];
    const hiddenCount = selected.length - maxVisible;
    const hiddenOptions = hiddenCount > 0 ? selected.slice(maxVisible) : [];
    const [chipArray, input] = Array.isArray(children) ? children : [children, null];
    const visibleChips = Array.isArray(chipArray) && hiddenCount > 0 ? chipArray.slice(0, maxVisible) : chipArray;
    const isDisabled = selectProps.isDisabled;

    const removeHiddenOption = (option) => {
        const newValue = selected.filter((o) => o.value !== option.value);
        selectProps.onChange(newValue, { action: 'remove-value', removedValue: option, name: selectProps.name });
    };

    return (
        <components.ValueContainer {...props}>
            {visibleChips}
            <Badge
                id={`${idPrefix}-more`}
                color="light"
                pill
                className="text-muted border font-weight-normal"
                style={{ cursor: 'default', display: hiddenCount > 0 ? undefined : 'none' }}
                onMouseDown={(e) => e.stopPropagation()}>
                +{hiddenCount} more
            </Badge>
            {isDisabled ? (
                <UncontrolledTooltip target={`${idPrefix}-more`} placement="top">
                    {hiddenOptions.map((option) => option.label).join(', ')}
                </UncontrolledTooltip>
            ) : (
                <Popover
                    placement="top"
                    flip={true}
                    boundariesElement="viewport"
                    positionFixed
                    isOpen={morePopoverOpen && hiddenCount > 0}
                    target={`${idPrefix}-more`}
                    toggle={() => setMorePopoverOpen((prev) => !prev)}
                    trigger="hover"
                    delay={{ show: 0, hide: 200 }}
                    container="body">
                    <PopoverBody
                        className="d-flex flex-wrap"
                        style={{ gap: '4px', maxWidth: '220px' }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}>
                        {hiddenOptions.map((option, index) => (
                            <Badge
                                key={index}
                                color="secondary"
                                pill
                                className="font-weight-normal d-flex align-items-center">
                                {option.label}
                                <Icon
                                    icon={closeIcon}
                                    width="12"
                                    height="12"
                                    className="ml-1"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => removeHiddenOption(option)}
                                />
                            </Badge>
                        ))}
                    </PopoverBody>
                </Popover>
            )}
            {input}
        </components.ValueContainer>
    );
};

const FacilityRoleValueContainer = createLimitedMultiValueContainer(2, 'facility-role-select-chips');

export const RoleBadgeList = ({
    roles = [],
    idPrefix,
    maxVisible = 3,
    editable = false,
    allowAdd = editable,
    availableRoles = [],
    onAddRole,
    onRemoveRole,
    label = 'Role:',
}) => {
    const [hoveredRole, setHoveredRole] = useState(null);
    const [addPopoverOpen, setAddPopoverOpen] = useState(false);
    const [roleToAdd, setRoleToAdd] = useState(null);
    const [morePopoverOpen, setMorePopoverOpen] = useState(false);

    if ((!roles || roles.length === 0) && !editable) return null;

    const visibleRoles = roles.slice(0, maxVisible);
    const hiddenRoles = roles.slice(maxVisible);
    const addableOptions = availableRoles.filter((option) => !roles.includes(option.value));

    const closeAddPopover = () => {
        setAddPopoverOpen(false);
        setRoleToAdd(null);
    };

    return (
        <div className="d-flex align-items-center flex-wrap mt-1" style={{ gap: '4px', whiteSpace: 'normal' }}>
            {/* {roles.length > 0 && label && <small className="text-muted mr-1 mb-0">{label}</small>} */}
            {visibleRoles.map((role, index) => (
                <Badge
                    key={index}
                    color="secondary"
                    pill
                    className="font-weight-normal d-flex align-items-center"
                    onMouseEnter={() => editable && setHoveredRole(role)}
                    onMouseLeave={() => editable && setHoveredRole((prev) => (prev === role ? null : prev))}>
                    {role}
                    {editable && hoveredRole === role && (
                        <Icon
                            icon={closeIcon}
                            width="12"
                            height="12"
                            className="ml-1"
                            style={{ cursor: 'pointer' }}
                            onClick={() => onRemoveRole && onRemoveRole(role)}
                        />
                    )}
                </Badge>
            ))}
            <Badge
                id={`${idPrefix}-more`}
                color="light"
                pill
                className="text-muted border font-weight-normal"
                style={{ cursor: 'default', display: hiddenRoles.length > 0 ? undefined : 'none' }}>
                +{hiddenRoles.length} more
            </Badge>
            {editable ? (
                <Popover
                    placement="top"
                    flip={true}
                    boundariesElement="viewport"
                    positionFixed
                    isOpen={morePopoverOpen && hiddenRoles.length > 0}
                    target={`${idPrefix}-more`}
                    toggle={() => setMorePopoverOpen((prev) => !prev)}
                    trigger="hover"
                    delay={{ show: 0, hide: 200 }}
                    container="body">
                    <PopoverBody
                        className="d-flex flex-wrap"
                        style={{ gap: '4px', maxWidth: '220px' }}>
                        {hiddenRoles.map((role, index) => (
                            <Badge
                                key={index}
                                color="secondary"
                                pill
                                className="font-weight-normal d-flex align-items-center">
                                {role}
                                <Icon
                                    icon={closeIcon}
                                    width="12"
                                    height="12"
                                    className="ml-1"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onRemoveRole && onRemoveRole(role)}
                                />
                            </Badge>
                        ))}
                    </PopoverBody>
                </Popover>
            ) : (
                <UncontrolledTooltip target={`${idPrefix}-more`} placement="top">
                    {hiddenRoles.join(', ')}
                </UncontrolledTooltip>
            )}
            {allowAdd && (
                <>
                    <Badge
                        id={`${idPrefix}-add`}
                        color="light"
                        className="text-muted border font-weight-normal d-flex align-items-center justify-content-center"
                        style={{
                            cursor: 'pointer',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            padding: 0,
                        }}
                        onClick={() => setAddPopoverOpen((prev) => !prev)}>
                        <Icon icon={plusIcon} width="12" height="12" />
                    </Badge>
                    <Popover
                        placement="right"
                        flip={true}
                        boundariesElement="viewport"
                        positionFixed
                        offset="0, 8"
                        isOpen={addPopoverOpen}
                        target={`${idPrefix}-add`}
                        toggle={closeAddPopover}
                        trigger="legacy"
                        container="body">
                        <PopoverBody style={{ minWidth: '220px' }}>
                            <Select
                                className="react-select"
                                classNamePrefix="react-select"
                                placeholder="Select role to add..."
                                menuPlacement="auto"
                                menuPosition="fixed"
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                value={roleToAdd}
                                onChange={(selection) => {
                                    if (!selection) return;
                                    onAddRole && onAddRole(selection.value);
                                    closeAddPopover();
                                }}
                                options={addableOptions}
                            />
                        </PopoverBody>
                    </Popover>
                </>
            )}
        </div>
    );
};

const UserFormModal = ({
    isOpen,
    toggle,
    mode,
    user,
    readOnly = false,
    setFaciToken,
    users,
    setUsers,
    contextUser,
    onUserCreated,
}) => {
    const { facilities } = useContext(SelectedFacilitiesContext);
    const { facilityUserRoles, refetchUserRoles } = useContext(UserRolesContext);
    let history = useHistory();

    const isUpdateMode = mode === 'update';

    const [email, setEmail] = useState('');
    const [lookupStatus, setLookupStatus] = useState('idle');
    const [lookupUser, setLookupUser] = useState(null);
    const [isDeletedUser, setIsDeletedUser] = useState(false);

    const [displayName, setDisplayName] = useState('');
    const [phone, setPhone] = useState('');
    const [title, setTitle] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const [validState, setValidState] = useState({
        displayName: true,
        phone: true,
        title: true,
        password: true,
        confirmPasswordMatches: true,
        facilities: true,
        facilityRoleOrTemplate: true,
    });

    const [userFacilities, setUserFacilities] = useState(null);
    const [pendingUserFacilities, setPendingUserFacilities] = useState([]);
    const [selectedFacility, setSelectedFacility] = useState([]);
    const [newUserFacilities, setNewUserFacilities] = useState([]);
    const [newUserTemplateRole, setNewUserTemplateRole] = useState(null);
    const [newUserFacilityRole, setNewUserFacilityRole] = useState(null);
    const [userRoleLoading, setUserRoleLoading] = useState(false);
    const [roleTemplates, setRoleTemplates] = useState([]);
    const [selectedRoleTemplates, setSelectedRoleTemplates] = useState({});
    const [standardUserRoles, setStandardUserRoles] = useState(null);
    const [selectedStandardRole, setSelectedStandardRole] = useState(null);
    const [standardRoleChipHovered, setStandardRoleChipHovered] = useState(false);
    const [facilityRoleOverrides, setFacilityRoleOverrides] = useState({});
    const [initialUserDetails, setInitialUserDetails] = useState({ displayName: '', phone: '', title: '' });
    const [hasPerformedAction, setHasPerformedAction] = useState(false);
    const [pendingBulkTemplate, setPendingBulkTemplate] = useState(null);
    const [pendingBulkRole, setPendingBulkRole] = useState(null);
    const [savedFacilitiesRoleGroup, setSavedFacilitiesRoleGroup] = useState(null);

    const activeUser = isUpdateMode ? user : lookupUser;
    const isKnownUser = isUpdateMode || lookupStatus === 'found';
    const showDetailFields = isUpdateMode || lookupStatus === 'found' || lookupStatus === 'not-found';

    const canManageFacilities = !readOnly && (isKnownUser ? canAddFacility(activeUser) : true);

    const isDetailsUnchanged =
        isKnownUser &&
        displayName === initialUserDetails.displayName &&
        phone === initialUserDetails.phone &&
        title === initialUserDetails.title;

    function canAddFacility(user_) {
        let selectedUserRoles = user_?.entityTypeAndRole || [];
        let signedInUserFacID = facilities?.map((f) => f.facID).map((facid) => ({ facid }));
        let hasMatchingFacid = signedInUserFacID.filter((r1) =>
            selectedUserRoles.some((r2) => r2.facid === r1.facid)
        );
        if (hasMatchingFacid.length === 0) return true;

        let matchingFacility = facilities?.filter((f) => hasMatchingFacid.some((m) => m.facid === f.facID));
        let allUserPermissions = matchingFacility.map((f) => f.permissions);
        return allUserPermissions.some((permissions) => permissions?.canAddFacilityToUser === true);
    }

    const checkIfUserIsAdmin = (facility) => {
        if (!facility || !facility.userType) return false;
        if (facility.userType?.toLowerCase() === 'pharmacyuser') return true;
        if (facility.userType?.toLowerCase() === 'superadmin') return true;
        if (facility.userType?.toLowerCase() === 'facilityuser') {
            return (facility.userRoles || []).some((r) => r?.toLowerCase() === 'facilityadmin');
        }
        return false;
    };

    useEffect(() => {
        if (!isOpen) return;

        refetchUserRoles && refetchUserRoles();

        SrxApi.get('/roletemplates', { params: { entity: 'facility' } })
            .then((res) => {
                const templates = res.data?.roleTemplates || res.data || [];
                setRoleTemplates(
                    templates.map((template) => ({
                        value: template.roleTemplateId,
                        label: template.templateName,
                        userRoles: template.userRoles || [],
                    }))
                );
            })
            .catch(() => setRoleTemplates([]));

        SrxApi.get('/roles', { params: { entity: 'standardUser' } })
            .then((res) => setStandardUserRoles(res.data?.roles || []))
            .catch(() => setStandardUserRoles([]));
    }, [isOpen]);

    function loadUserFacilities(forEmail) {
        setUserFacilities([]);
        SrxApi.get('/userfacilities', { params: { email: forEmail } })
            .then(({ data }) => {
                setUserFacilities(data.facilities);
                const existingStandardRole = data.facilities?.find((f) => f.standardRoles?.length)?.standardRoles?.[0];
                setSelectedStandardRole(existingStandardRole ? { value: existingStandardRole, label: existingStandardRole } : null);
            })
            .catch((err) => {
                if (err.response?.status === 401) {
                    localStorage.removeItem('FACI_TOKEN');
                    setFaciToken(null);
                    history.replace('/account/login');
                }
            });
    }

    useEffect(() => {
        if (!userFacilities?.length || !roleTemplates.length) return;
        const templateIds = new Set(userFacilities.map((f) => f.roleTemplate?.roleTemplateId).filter((id) => id != null));
        if (templateIds.size === 1) {
            const [templateId] = templateIds;
            const matchedTemplate = roleTemplates.find((t) => t.value === templateId);
            if (matchedTemplate) setSavedFacilitiesRoleGroup(matchedTemplate);
        }
    }, [userFacilities, roleTemplates]);

    useEffect(() => {
        const templateRoles = savedFacilitiesRoleGroup?.userRoles || [];
        if (!templateRoles.length || !userFacilities?.length) return;
        const editableFacilities = getEditableFacilities();
        if (!editableFacilities.length) return;
        const stillApplied = templateRoles.some((role) =>
            editableFacilities.every((facility) => getDisplayedRolesForFacility(facility).includes(role))
        );
        if (!stillApplied) setSavedFacilitiesRoleGroup(null);
    }, [savedFacilitiesRoleGroup, userFacilities, facilityRoleOverrides, selectedRoleTemplates]);

    useEffect(() => {
        if (!isOpen) return;

        setFacilityRoleOverrides({});
        setSelectedRoleTemplates({});
        setSelectedStandardRole(null);
        setPendingUserFacilities([]);
        setSelectedFacility([]);
        setNewUserFacilities([]);
        setNewUserTemplateRole(null);
        setNewUserFacilityRole(null);
        setHasPerformedAction(false);
        setPendingBulkTemplate(null);
        setSavedFacilitiesRoleGroup(null);
        setValidState({
            displayName: true,
            phone: true,
            title: true,
            password: true,
            confirmPasswordMatches: true,
            facilities: true,
            facilityRoleOrTemplate: true,
        });
        setPassword('');
        setConfirmPassword('');

        if (isUpdateMode) {
            setEmail(user?.email || '');
            setDisplayName(user?.displayName || '');
            setPhone(user?.phone || '');
            setTitle(user?.title || '');
            setInitialUserDetails({
                displayName: user?.displayName || '',
                phone: user?.phone || '',
                title: user?.title || '',
            });
            setLookupStatus('found');
            setLookupUser(null);
            setIsDeletedUser(false);
            loadUserFacilities(user?.email);
        } else {
            setEmail('');
            setDisplayName('');
            setPhone('');
            setTitle('');
            setInitialUserDetails({ displayName: '', phone: '', title: '' });
            setLookupStatus('idle');
            setLookupUser(null);
            setIsDeletedUser(false);
            setUserFacilities(null);
        }
    }, [isOpen, user]);

    const emailDebounceRef = useRef(null);

    function handleEmailChange(value) {
        setEmail(value);

        if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);

        if (!value || !/\S+@\S+\.\S+/.test(value)) {
            setLookupStatus('idle');
            setLookupUser(null);
            setIsDeletedUser(false);
            setUserFacilities(null);
            return;
        }

        emailDebounceRef.current = setTimeout(() => runEmailLookup(value), 500);
    }

    function runEmailLookup(value) {
        setLookupStatus('loading');
        SrxApi.get('/userlookup', { params: { email: value } })
            .then(({ data }) => {
                if (data?.exists) {
                    setLookupUser(data);
                    setIsDeletedUser(!!data.isDeleted);
                    setLookupStatus('found');
                    setDisplayName(data.displayName || '');
                    setPhone(data.phone || '');
                    setTitle(data.title || '');
                    setInitialUserDetails({
                        displayName: data.displayName || '',
                        phone: data.phone || '',
                        title: data.title || '',
                    });
                    loadUserFacilities(value);
                } else {
                    setLookupUser(null);
                    setIsDeletedUser(false);
                    setLookupStatus('not-found');
                    setDisplayName('');
                    setPhone('');
                    setTitle('');
                    setUserFacilities([]);
                }
            })
            .catch(() => {
                setLookupStatus('not-found');
                setLookupUser(null);
                setIsDeletedUser(false);
                setUserFacilities([]);
            });
    }

    function onFaciRoleTemplateChange(roleTemplateId, forEmail, facility) {
        SrxApi.post('/edituser', { entityID: facility.facID, email: forEmail, RoleTemplateId: roleTemplateId })
            .then(() => toast.success('User role successfully changed'))
            .catch((err) => toast.error('Error: ' + err.message));
    }

    function removeRoleGroupFromAllFacilities(roleGroup) {
        const templateRoles = roleGroup?.userRoles || [];
        setSavedFacilitiesRoleGroup(null);

        if (!templateRoles.length) return;

        const editableFacilities = getEditableFacilities().filter((facility) =>
            templateRoles.some((role) => getDisplayedRolesForFacility(facility).includes(role))
        );

        if (!editableFacilities.length) return;

        setHasPerformedAction(true);

        editableFacilities.forEach((facility) => {
            const current = getDisplayedRolesForFacility(facility);
            setFacilityRoleOverrides((prev) => ({
                ...prev,
                [facility.facID]: (prev[facility.facID] ?? current).filter((r) => !templateRoles.includes(r)),
            }));
        });

        Promise.all(
            editableFacilities.flatMap((facility) =>
                templateRoles
                    .filter((role) => getDisplayedRolesForFacility(facility).includes(role))
                    .map((role) =>
                        SrxApi.post('/removeuserrole', { entityID: facility.facID, email, UserRole: role })
                            .then(() => true)
                            .catch(() => false)
                    )
            )
        ).then((results) => {
            const succeeded = results.filter(Boolean).length;
            const failed = results.length - succeeded;
            if (failed === 0) {
                toast.success('Role group removed from all facilities');
            } else {
                toast.error(`Role group removed, but ${failed} update${failed === 1 ? '' : 's'} failed`);
            }
        });
    }

    function applyRoleGroupToAllFacilities(selection) {
        if (!selection) {
            removeRoleGroupFromAllFacilities(savedFacilitiesRoleGroup);
            return;
        }

        const editableFacilities = (userFacilities || []).filter((facility) => {
            const isRoleEditingDisabled =
                readOnly ||
                userRoleLoading ||
                !checkIfUserIsAdmin(facilities.find((faci) => faci.facID === facility.facID)) ||
                email === contextUser.email;
            return !isRoleEditingDisabled;
        });

        if (!editableFacilities.length) {
            toast.error('No facilities available to update');
            return;
        }

        setHasPerformedAction(true);
        setSavedFacilitiesRoleGroup(selection);

        editableFacilities.forEach((facility) => {
            const pickedTemplate = selectedRoleTemplates[facility.facID];
            const oldTemplateRoles = pickedTemplate ? pickedTemplate.userRoles || [] : facility.templateRoles || [];
            const current = getDisplayedRolesForFacility(facility);
            const extraRoles = current.filter((r) => !oldTemplateRoles.includes(r));
            const newRoles = Array.from(new Set([...(selection.userRoles || []), ...extraRoles]));

            setSelectedRoleTemplates((prev) => ({ ...prev, [facility.facID]: selection }));
            setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: newRoles }));
        });

        Promise.all(
            editableFacilities.map((facility) =>
                SrxApi.post('/edituser', { entityID: facility.facID, email, roleTemplateId: selection.value })
                    .then(() => true)
                    .catch(() => false)
            )
        ).then((results) => {
            const succeeded = results.filter(Boolean).length;
            const failed = results.length - succeeded;
            if (failed === 0) {
                toast.success(`Role group applied to ${succeeded} facilit${succeeded === 1 ? 'y' : 'ies'}`);
            } else {
                toast.error(`Role group applied to ${succeeded} of ${results.length} facilities, ${failed} failed`);
            }
        });
    }

    function getDisplayedRolesForFacility(facility) {
        const pickedTemplate = selectedRoleTemplates[facility.facID];
        const templateRoles = pickedTemplate ? pickedTemplate.userRoles || [] : facility.templateRoles || [];
        const combinedRoles = Array.from(
            new Set([...templateRoles, ...(facility.userRoles || []), ...(facility.userRole ? [facility.userRole] : [])])
        );
        return (facilityRoleOverrides[facility.facID] ?? combinedRoles).filter((r) => r !== selectedStandardRole?.value);
    }

    function getActiveRoleGroupRoles() {
        const templateRoles = savedFacilitiesRoleGroup?.userRoles || [];
        if (!templateRoles.length) return [];
        const editableFacilities = getEditableFacilities();
        if (!editableFacilities.length) return [];
        return templateRoles.filter((role) =>
            editableFacilities.every((facility) => getDisplayedRolesForFacility(facility).includes(role))
        );
    }

    function getFacilityRolesOutsideTemplate(facility) {
        const activeRoleGroupRoles = getActiveRoleGroupRoles();
        return getDisplayedRolesForFacility(facility).filter((r) => !activeRoleGroupRoles.includes(r));
    }

    function getRolesOnAllFacilities() {
        const [firstRoles, ...otherRoles] = getEditableFacilities().map(getFacilityRolesOutsideTemplate);
        return (firstRoles || []).filter((role) => otherRoles.every((roles) => roles.includes(role)));
    }

    function getEditableFacilities() {
        return (userFacilities || []).filter((facility) => {
            const isRoleEditingDisabled =
                readOnly ||
                userRoleLoading ||
                !checkIfUserIsAdmin(facilities.find((faci) => faci.facID === facility.facID)) ||
                email === contextUser.email;
            return !isRoleEditingDisabled;
        });
    }

    function onRolesForAllFacilitiesChange(_, { action, option, removedValue }) {
        if (action === 'select-option') applyRoleToAllFacilities(option.value);
        if (action === 'remove-value') removeRoleFromAllFacilities(removedValue.value);
    }

    function applyRoleToAllFacilities(roleValue) {
        const editableFacilities = getEditableFacilities();

        if (!editableFacilities.length) {
            toast.error('No facilities available to update');
            return;
        }

        setHasPerformedAction(true);

        editableFacilities.forEach((facility) => {
            const current = getDisplayedRolesForFacility(facility);
            if (current.includes(roleValue)) return;
            setFacilityRoleOverrides((prev) => ({
                ...prev,
                [facility.facID]: [...(prev[facility.facID] ?? current), roleValue],
            }));
        });

        Promise.all(
            editableFacilities.map((facility) =>
                SrxApi.post('/edituser', { entityID: facility.facID, email, UserRole: roleValue })
                    .then(() => true)
                    .catch(() => false)
            )
        ).then((results) => {
            const succeeded = results.filter(Boolean).length;
            const failed = results.length - succeeded;
            if (failed === 0) {
                toast.success(`Role applied to ${succeeded} facilit${succeeded === 1 ? 'y' : 'ies'}`);
            } else {
                toast.error(`Role applied to ${succeeded} of ${results.length} facilities, ${failed} failed`);
            }
        });
    }

    function removeRoleFromAllFacilities(roleValue) {
        const editableFacilities = getEditableFacilities().filter((facility) =>
            getDisplayedRolesForFacility(facility).includes(roleValue)
        );

        if (!editableFacilities.length) {
            toast.error('No facilities available to update');
            return;
        }

        setHasPerformedAction(true);

        editableFacilities.forEach((facility) => {
            const current = getDisplayedRolesForFacility(facility);
            setFacilityRoleOverrides((prev) => ({
                ...prev,
                [facility.facID]: (prev[facility.facID] ?? current).filter((r) => r !== roleValue),
            }));
        });

        Promise.all(
            editableFacilities.map((facility) =>
                SrxApi.post('/removeuserrole', { entityID: facility.facID, email, UserRole: roleValue })
                    .then(() => true)
                    .catch(() => false)
            )
        ).then((results) => {
            const succeeded = results.filter(Boolean).length;
            const failed = results.length - succeeded;
            if (failed === 0) {
                toast.success(`Role removed from ${succeeded} facilit${succeeded === 1 ? 'y' : 'ies'}`);
            } else {
                toast.error(`Role removed from ${succeeded} of ${results.length} facilities, ${failed} failed`);
            }
        });
    }

    function onFaciUserRoleChange(roleValue, forEmail, facility) {
        return SrxApi.post('/edituser', { entityID: facility.facID, email: forEmail, UserRole: roleValue })
            .then(() => toast.success('Role added'))
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Error: ' + err.message);
                throw err;
            });
    }

    function removeFaciUserRole(roleValue, forEmail, facility) {
        return SrxApi.post('/removeuserrole', { entityID: facility.facID, email: forEmail, UserRole: roleValue })
            .then(() => toast.success('Role removed'))
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Error: ' + err.message);
                throw err;
            });
    }

    const addFacilityRole = (facility, baseRoles, roleValue) => {
        const current = facilityRoleOverrides[facility.facID] ?? baseRoles;
        if (current.includes(roleValue)) return;
        setHasPerformedAction(true);
        setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: [...current, roleValue] }));
        onFaciUserRoleChange(roleValue, email, facility).catch(() =>
            setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: current }))
        );
    };

    const removeFacilityRole = (facility, baseRoles, roleValue) => {
        const current = facilityRoleOverrides[facility.facID] ?? baseRoles;
        setHasPerformedAction(true);
        setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: current.filter((r) => r !== roleValue) }));

        const wasSelectedStandardRole = selectedStandardRole?.value === roleValue;
        if (wasSelectedStandardRole) setSelectedStandardRole(null);

        removeFaciUserRole(roleValue, email, facility).catch(() => {
            setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: current }));
            if (wasSelectedStandardRole) setSelectedStandardRole({ value: roleValue, label: roleValue });
        });
    };

    const getFacilityRoleDisplay = (facility) => {
        const currentTemplate =
            selectedRoleTemplates[facility.facID] ||
            (facility.roleTemplate
                ? {
                      value: facility.roleTemplate.roleTemplateId,
                      label: facility.roleTemplate.templateName,
                      userRoles: facility.roleTemplate.userRoles || [],
                  }
                : null);
        const pickedTemplate = selectedRoleTemplates[facility.facID];
        const templateRoles = pickedTemplate ? pickedTemplate.userRoles || [] : facility.templateRoles || [];
        const combinedRoles = Array.from(
            new Set([...templateRoles, ...(facility.userRoles || []), ...(facility.userRole ? [facility.userRole] : [])])
        );
        const displayedRoles = (facilityRoleOverrides[facility.facID] ?? combinedRoles).filter(
            (r) => r !== selectedStandardRole?.value
        );
        return { currentTemplate, displayedRoles };
    };

    const addPendingFacilityRole = (facility, baseRoles, roleValue) => {
        const current = facilityRoleOverrides[facility.facID] ?? baseRoles;
        if (current.includes(roleValue)) return;
        setHasPerformedAction(true);
        setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: [...current, roleValue] }));
    };

    const removePendingFacilityRole = (facility, baseRoles, roleValue) => {
        const current = facilityRoleOverrides[facility.facID] ?? baseRoles;
        setHasPerformedAction(true);
        setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: current.filter((r) => r !== roleValue) }));
    };

    const assignStandardRole = (selection) => {
        const previousRoleValue = selectedStandardRole?.value;
        setSelectedStandardRole(selection);

        const roleValue = selection?.value ?? null;
        if (roleValue === previousRoleValue) return;

        setHasPerformedAction(true);

        (userFacilities || []).forEach((facility) => {
            const current =
                facilityRoleOverrides[facility.facID] ??
                Array.from(
                    new Set([
                        ...(facility.templateRoles || []),
                        ...(facility.userRoles || []),
                        ...(facility.userRole ? [facility.userRole] : []),
                        ...(facility.standardRoles || []),
                    ])
                );

            const hadPrevious = !!previousRoleValue && current.includes(previousRoleValue);
            const alreadyHasNew = !!roleValue && current.includes(roleValue);
            if (!hadPrevious && (alreadyHasNew || !roleValue)) return;

            const updated = current.filter((r) => r !== previousRoleValue);
            if (roleValue && !alreadyHasNew) updated.push(roleValue);

            setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: updated }));

            const revert = () => setFacilityRoleOverrides((prev) => ({ ...prev, [facility.facID]: current }));

            (async () => {
                try {
                    if (hadPrevious) await removeFaciUserRole(previousRoleValue, email, facility);
                    if (roleValue && !alreadyHasNew) await onFaciUserRoleChange(roleValue, email, facility);
                } catch {
                    revert();
                }
            })();
        });
    };

    function addToPendingFacilities() {
        let tempSelected = cloneDeep(selectedFacility[0]);
        delete tempSelected.userRole;
        delete tempSelected.userRoles;
        delete tempSelected.templateRoles;
        delete tempSelected.roleTemplate;
        delete tempSelected.standardRoles;
        setHasPerformedAction(true);
        setPendingUserFacilities([...pendingUserFacilities, tempSelected]);
        setSelectedFacility([]);
        setValidState((prev) => ({ ...prev, facilities: true }));
        if (pendingBulkTemplate) {
            setSelectedRoleTemplates((prev) => ({ ...prev, [tempSelected.facID]: pendingBulkTemplate }));
        }
    }

    function applyBulkTemplateToPending(selection) {
        setHasPerformedAction(true);
        setPendingBulkTemplate(selection);
        setSelectedRoleTemplates((prev) => {
            const next = { ...prev };
            pendingUserFacilities.forEach(({ facID }) => {
                next[facID] = selection;
            });
            return next;
        });
    }

    function applyBulkRoleToPending(selection) {
        if (!selection) {
            setPendingBulkRole(null);
            return;
        }
        setHasPerformedAction(true);
        setPendingBulkRole(selection);
        pendingUserFacilities.forEach((facility) => {
            const { displayedRoles } = getFacilityRoleDisplay(facility);
            if (displayedRoles.includes(selection.value)) return;
            setFacilityRoleOverrides((prev) => ({
                ...prev,
                [facility.facID]: [...(prev[facility.facID] ?? displayedRoles), selection.value],
            }));
        });
    }

    function deletePendingFacility(id) {
        setHasPerformedAction(true);
        setPendingUserFacilities(pendingUserFacilities.filter(({ facID }) => facID !== id));
        setFacilityRoleOverrides((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setSelectedRoleTemplates((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }

    function saveAdditionalPendingFacilities(forEmail) {
        if (!pendingUserFacilities.length) return Promise.resolve();

        const facilitiesToSave = pendingUserFacilities.map((facility) => ({ facility, ...getFacilityRoleDisplay(facility) }));

        if (facilitiesToSave.some(({ displayedRoles }) => !displayedRoles.length)) {
            toast.error('Please assign at least one role to each pending facility');
            return Promise.reject();
        }

        return SrxApi.post('/adduserfacilities', {
            email: forEmail,
            entities: facilitiesToSave.map(({ facility, displayedRoles }) => ({ entityID: facility.facID, userRole: displayedRoles[0] })),
            ...(isDeletedUser ? { isDeleted: false } : {}),
        })
            .then(() =>
                Promise.all(
                    // One /edituser per facility carrying the template and any extra roles together -
                    // separate parallel calls for the same facility added the template's roles twice
                    // and one of them always failed with "already on Faci with this role".
                    facilitiesToSave.map(({ facility, currentTemplate, displayedRoles }) => {
                        const templateRoles = currentTemplate?.userRoles || [];
                        const extraRoles = displayedRoles.slice(1).filter((role) => !templateRoles.includes(role));
                        if (!currentTemplate && !extraRoles.length) return Promise.resolve();

                        return SrxApi.post('/edituser', {
                            entityID: facility.facID,
                            email: forEmail,
                            RoleTemplateId: currentTemplate?.value ?? null,
                            UserRoles: extraRoles,
                        });
                    })
                )
            )
            .then(() => {
                loadUserFacilities(forEmail);
                setFacilityRoleOverrides((prev) => {
                    const next = { ...prev };
                    pendingUserFacilities.forEach(({ facID }) => delete next[facID]);
                    return next;
                });
                setSelectedRoleTemplates((prev) => {
                    const next = { ...prev };
                    pendingUserFacilities.forEach(({ facID }) => delete next[facID]);
                    return next;
                });
                setPendingUserFacilities([]);
                setPendingBulkTemplate(null);
                toast.success('User added to facilities');
            });
    }

    async function removeUserFromSingleFacility(id, name) {
        if (
            !(await confirm(
                `Remove <i>${displayName || email}</i> from <i>${name}</i>?` +
                    `<div style="background-color:#fff3cd;border:1px solid #ffeeba;border-radius:4px;color:#856404;padding:8px;margin-top:10px;font-size:14px;">` +
                    `Removing them from <i>${name}</i> will also remove them from alert notifications` +
                    `</div>`,
                `Removing user from facility`,
                'Confirm',
                'Cancel',
                'primary',
                'primary'
            ))
        )
            return;

        SrxApi.post('/removefacility', { email, facID: id })
            .then(() => {
                setHasPerformedAction(true);

                const newFacilities = userFacilities.filter(({ facID }) => facID !== id);
                setUserFacilities(newFacilities);

                const tempUsers = [...users];
                const matchedTempUser = tempUsers.find((u) => u.email === email);
                if (matchedTempUser) matchedTempUser.numberFacilities = newFacilities.length;
                setUsers(tempUsers);

                toast.success((displayName || email) + ' removed from ' + name);
            })
            .catch((err) => toast.error('Error: ' + err.message));
    }

    function validateForm() {
        const newValidState = { ...validState };
        let valid = true;

        if (!displayName || displayName.length < 4) newValidState.displayName = false;
        else newValidState.displayName = true;

        const digits = phone.replace(/\D/g, '');
        newValidState.phone = digits.length === 0 || digits.length === 10;

        if (!title) newValidState.title = false;
        else newValidState.title = true;

        if (!isKnownUser) {
            newValidState.password = !!password;
            newValidState.confirmPasswordMatches = password === confirmPassword;
            newValidState.facilities = newUserFacilities.length > 0;
            newValidState.facilityRoleOrTemplate = !!(newUserFacilityRole || newUserTemplateRole);
        } else {
            newValidState.password = true;
            newValidState.confirmPasswordMatches = true;
            newValidState.facilities = true;
            newValidState.facilityRoleOrTemplate = true;
        }

        for (let key in newValidState) {
            if (newValidState[key] === false) valid = false;
        }

        setValidState(newValidState);
        return valid;
    }

    function handleSave() {
        if (!validateForm()) return;
        setSaving(true);

        if (!isKnownUser) {
            const [firstFacility, ...restFacilities] = newUserFacilities;
            const payload = {
                displayName,
                email,
                phone: phone.replace(/\D/g, ''),
                Title: title,
                password,
                confirmPassword,
                faciID: firstFacility.value,
                UserRole: newUserFacilityRole?.value || null,
                RoleTemplateId: newUserTemplateRole?.value || null,
                UserType: 'FacilityUser',
            };

            SrxApi.post('/createuser', payload)
                .then(async (res) => {
                    if (restFacilities.length) {
                        const bootstrapRole = newUserFacilityRole?.value || (newUserTemplateRole?.userRoles || [])[0] || null;
                        await SrxApi.post('/adduserfacilities', {
                            email,
                            entities: restFacilities.map((f) => ({ entityID: f.value, userRole: bootstrapRole })),
                        }).catch(() => {});

                        if (newUserTemplateRole) {
                            await Promise.all(
                                restFacilities.map((f) => onFaciRoleTemplateChange(newUserTemplateRole.value, email, { facID: f.value }))
                            ).catch(() => {});
                        }
                    }

                    const createdUser = {
                        displayName,
                        email,
                        phone: payload.phone,
                        numberFacilities: newUserFacilities.length,
                        entityTypeAndRole: newUserFacilities.map((f) => ({ facid: f.value, userRole: newUserFacilityRole?.value || null })),
                        ...(res?.data?.user || res?.data?.userInfo || {}),
                    };

                    onUserCreated && onUserCreated(createdUser);
                    toast.success('Facility user successfully created');
                    toggle();
                })
                .catch((err) => toast.error(err.response?.data?.message || 'An error occurred'))
                .finally(() => setSaving(false));
            return;
        }

        const finalizeDetailsAndFacilities = () => {
            const detailsPromise = SrxApi.post('/edituserinfo', {
                displayName,
                phone: phone.replace(/\D/g, ''),
                Title: title,
                UserRole: activeUser?.entityTypeAndRole?.[0]?.userRole,
                email,
                entityId: activeUser?.entityTypeAndRole?.[0]?.facid,
            });

            detailsPromise
                .then(() => saveAdditionalPendingFacilities(email))
                .then(() => {
                    const updatedUsers = (users || []).map((u) =>
                        u.email === email
                            ? { ...u, displayName, phone: phone.replace(/\D/g, ''), title }
                            : u
                    );
                    setUsers(updatedUsers);
                    toast.success('User updated successfully');
                    toggle();
                })
                .catch((err) => toast.error(err.response?.data?.message || err.message))
                .finally(() => setSaving(false));
        };

        restoreDeletedUserIfNeeded()
            .then(finalizeDetailsAndFacilities)
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Error restoring user');
                setSaving(false);
            });
    }

    // A deleted user has to be restored before anything else can be saved for them - their email in
    // the DB still carries the delete suffix until then, so /adduserfacilities etc. can't find them.
    function restoreDeletedUserIfNeeded() {
        if (!isDeletedUser) return Promise.resolve();

        return SrxApi.post('/undeleteuser', { email }).then(() => {
            // restored once - a retry after a later step fails must not try to restore again
            setIsDeletedUser(false);
            toast.success((displayName || email) + ' restored');
        });
    }

    function handleSavePendingFacilities() {
        restoreDeletedUserIfNeeded()
            .then(() => saveAdditionalPendingFacilities(email))
            .catch((err) => {
                // saveAdditionalPendingFacilities rejects with nothing when it already showed its own message
                if (err) toast.error(err.response?.data?.message || err.message);
            });
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="lg"
            style={{
                minWidth: '600px',
                maxWidth: 'max-content',
                width: '100%',
                ...(showDetailFields ? { minHeight: '500px' } : {}),
            }}
            className="modal-dialog-scrollable">
            <ModalHeader toggle={toggle} className="modal-colored-header bg-primary">
                {isUpdateMode ? (
                    <>
                        {readOnly ? 'View Facilities for' : 'Update User'} <i>"{user?.displayName || user?.email}"</i>
                        {user?.email === contextUser.email && (
                            <Badge color="dark" className="ml-1" style={{ fontSize: '14px' }}>
                                Self
                            </Badge>
                        )}
                    </>
                ) : (
                    'Add Facility User'
                )}
            </ModalHeader>
            <ModalBody>
                {!isUpdateMode && (
                    <FormGroup>
                        <Label for="add-user-email">Email *</Label>
                        <Input
                            type="email"
                            id="add-user-email"
                            placeholder="Enter email to search or add a new user..."
                            value={email}
                            onChange={(e) => handleEmailChange(e.target.value)}
                        />
                        {lookupStatus === 'loading' && <small className="text-muted">Looking up email...</small>}
                        {lookupStatus === 'found' && !isDeletedUser && (
                            <small className="text-success">Existing user found — details and facilities loaded below.</small>
                        )}
                        {lookupStatus === 'found' && isDeletedUser && (
                            <small className="text-warning">
                                This user was previously deleted. Saving will restore them.
                            </small>
                        )}
                        {lookupStatus === 'not-found' && (
                            <small className="text-muted">No existing user found — fill out the details below to create one.</small>
                        )}
                    </FormGroup>
                )}

                {showDetailFields && (
                    <>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label style={{ color: !validState.displayName ? '#fa5c7c' : '' }}>Display Name *</Label>
                                    <Input
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="Display name..."
                                        disabled={readOnly}
                                    />
                                    {!validState.displayName && (
                                        <div className="invalid-text">Name must include a minimum of 4 characters</div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label style={{ color: !validState.phone ? '#fa5c7c' : '' }}>Phone</Label>
                                    <MaskedInput
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                                        placeholder="(xxx) xxxx-xxxx"
                                        className={`form-control ${!validState.phone ? 'invalid' : ''}`}
                                        disabled={readOnly}
                                    />
                                    {!validState.phone && <div className="invalid-text">Invalid phone</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label style={{ color: !validState.title ? '#fa5c7c' : '' }}>Title *</Label>
                                    <Input
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Title..."
                                        disabled={readOnly}
                                    />
                                    {!validState.title && <div className="invalid-text">Please enter a title</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        {!isKnownUser && (
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label style={{ color: !validState.password ? '#fa5c7c' : '' }}>Password *</Label>
                                        <Input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Password..."
                                        />
                                        {!validState.password && <div className="invalid-text">Please enter a password</div>}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label style={{ color: !validState.confirmPasswordMatches ? '#fa5c7c' : '' }}>
                                            Confirm Password *
                                        </Label>
                                        <Input
                                            type="password"
                                            autoComplete="off"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Re-enter Password..."
                                        />
                                        {!validState.confirmPasswordMatches && (
                                            <div className="invalid-text">Passwords do not match</div>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}

                        <hr />

                        {isKnownUser && canManageFacilities && (
                            <FormGroup>
                                <Label style={{ color: !validState.facilities ? '#fa5c7c' : '' }}>Select Facility</Label>
                                <InputGroup>
                                    <div style={{ flex: 1 }}>
                                        <Select
                                            className="react-select"
                                            classNamePrefix="react-select"
                                            onChange={(selection) => setSelectedFacility(selection ? [selection] : [])}
                                            value={selectedFacility[0] || null}
                                            getOptionValue={(faci) => faci.facID}
                                            getOptionLabel={(faci) => faci.facName}
                                            filterOption={(option, inputValue) =>
                                                option.label.toLowerCase().includes(inputValue.toLowerCase())
                                            }
                                            options={facilities
                                                .filter((faci) => faci.permissions?.canAddFacilityToUser === true)
                                                .filter(
                                                    (faci) =>
                                                        (userFacilities || []).findIndex((uf) => uf.facID === faci.facID) === -1 &&
                                                        pendingUserFacilities.findIndex((uf) => uf.facID === faci.facID) === -1
                                                )}
                                            placeholder="Select facility..."
                                        />
                                    </div>
                                    <InputGroupAddon addonType="append">
                                        <Button color="primary" onClick={addToPendingFacilities} disabled={!selectedFacility.length}>
                                            Add
                                        </Button>
                                    </InputGroupAddon>
                                </InputGroup>
                                {!validState.facilities && <div className="invalid-text">Please add at least one facility</div>}
                            </FormGroup>
                        )}

                        {!isKnownUser && (
                            <>
                                <FormGroup>
                                    <Label style={{ color: !validState.facilities ? '#fa5c7c' : '' }}>Facilities *</Label>
                                    <Select
                                        isMulti
                                        className={`${!validState.facilities ? 'invalid-select' : 'react-select'}`}
                                        classNamePrefix="react-select"
                                        isLoading={!facilities}
                                        placeholder="Select Facilities..."
                                        onChange={(selection) => {
                                            setValidState((prev) => ({ ...prev, facilities: true }));
                                            setNewUserFacilities(selection ? [...selection] : []);
                                        }}
                                        value={newUserFacilities}
                                        filterOption={(option, inputValue) =>
                                            option.label.toLowerCase().includes(inputValue.toLowerCase())
                                        }
                                        options={(facilities || [])
                                            .filter((faci) => faci.permissions?.canAddFacilityToUser === true)
                                            .map((faci) => ({ value: faci.facID, label: faci.facName }))}
                                    />
                                    {!validState.facilities && <div className="invalid-text">Please select at least one facility</div>}
                                </FormGroup>
                                <FormGroup>
                                    <Label style={{ color: !validState.facilityRoleOrTemplate ? '#fa5c7c' : '' }}>Role Group</Label>
                                    <Select
                                        className="react-select"
                                        classNamePrefix="react-select"
                                        isLoading={!roleTemplates}
                                        placeholder="Select Role Group..."
                                        isClearable
                                        isSearchable={false}
                                        onChange={(selection) => {
                                            setValidState((prev) => ({ ...prev, facilityRoleOrTemplate: true }));
                                            setNewUserTemplateRole(selection || null);
                                            if (selection?.userRoles?.includes(newUserFacilityRole?.value)) setNewUserFacilityRole(null);
                                        }}
                                        value={newUserTemplateRole}
                                        options={roleTemplates}
                                    />
                                    {newUserTemplateRole?.userRoles?.length > 0 && (
                                        <RoleBadgeList
                                            roles={newUserTemplateRole.userRoles}
                                            idPrefix="add-user-facility-role-badges"
                                        />
                                    )}
                                </FormGroup>
                                <FormGroup>
                                    <Label style={{ color: !validState.facilityRoleOrTemplate ? '#fa5c7c' : '' }}>Facility Role</Label>
                                    <Select
                                        className={`${!validState.facilityRoleOrTemplate ? 'invalid-select' : 'react-select'}`}
                                        classNamePrefix="react-select"
                                        isSearchable={false}
                                        isClearable
                                        placeholder="Select Role..."
                                        onChange={(selection) => {
                                            setValidState((prev) => ({ ...prev, facilityRoleOrTemplate: true }));
                                            setNewUserFacilityRole(selection || null);
                                        }}
                                        value={newUserFacilityRole}
                                        options={(facilityUserRoles || []).filter((role) => !newUserTemplateRole?.userRoles?.includes(role.value))}
                                    />
                                    {!validState.facilityRoleOrTemplate && (
                                        <div className="invalid-text">Please select a Facility Role or a Role Group</div>
                                    )}
                                </FormGroup>
                            </>
                        )}

                        {isKnownUser && (
                            <FormGroup>
                                <Label for="standard-role">Standard Role</Label>
                                <Select
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    name="standard-role"
                                    isLoading={!standardUserRoles}
                                    isClearable
                                    isSearchable={false}
                                    isDisabled={
                                        readOnly ||
                                        userRoleLoading ||
                                        email === contextUser.email ||
                                        !(userFacilities || []).some((facility) =>
                                            checkIfUserIsAdmin(facilities.find((faci) => faci.facID === facility.facID))
                                        )
                                    }
                                    placeholder="Select Standard Role..."
                                    value={selectedStandardRole}
                                    onChange={assignStandardRole}
                                    options={(standardUserRoles || []).map((role) => ({ value: role.name, label: role.label || role.name }))}
                                />
                                {selectedStandardRole && (
                                    <Badge
                                        color="secondary"
                                        pill
                                        className="font-weight-normal mt-2 d-inline-flex align-items-center"
                                        onMouseEnter={() => setStandardRoleChipHovered(true)}
                                        onMouseLeave={() => setStandardRoleChipHovered(false)}>
                                        {selectedStandardRole.label}
                                        {!readOnly && standardRoleChipHovered && (
                                            <Icon
                                                icon={closeIcon}
                                                width="12"
                                                height="12"
                                                className="ml-1"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => assignStandardRole(null)}
                                            />
                                        )}
                                    </Badge>
                                )}
                            </FormGroup>
                        )}

                        {!!pendingUserFacilities.length && (
                            <Card>
                                <CardBody className="p-2 m-2" style={{ border: '2px dashed #BDC6CA', borderRadius: '3px' }}>
                                    <h4 className="ml-1">{isKnownUser ? 'Pending Facilities' : 'Facilities'}</h4>
                                    {isKnownUser && pendingUserFacilities.length > 1 && (
                                        <div className="d-flex" style={{ gap: '16px' }}>
                                            <FormGroup className="ml-1 mr-1" style={{ flex: 1 }}>
                                                <Label>Role Template for all pending facilities</Label>
                                                <Select
                                                    className="react-select"
                                                    classNamePrefix="react-select"
                                                    hideSelectedOptions
                                                    isSearchable={false}
                                                    placeholder="Select template..."
                                                    onChange={applyBulkTemplateToPending}
                                                    value={pendingBulkTemplate}
                                                    options={roleTemplates}
                                                />
                                            </FormGroup>
                                            <FormGroup className="ml-1 mr-1" style={{ flex: 1 }}>
                                                <Label>Role for all pending facilities</Label>
                                                <Select
                                                    className="react-select"
                                                    classNamePrefix="react-select"
                                                    hideSelectedOptions
                                                    isSearchable={false}
                                                    isClearable
                                                    placeholder="Select role..."
                                                    onChange={applyBulkRoleToPending}
                                                    value={pendingBulkRole}
                                                    options={facilityUserRoles || []}
                                                />
                                            </FormGroup>
                                        </div>
                                    )}
                                    <Table className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Facility ID</th>
                                                <th>Facility Name</th>
                                                <th>Role</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody style={{ overflow: 'auto', whiteSpace: 'nowrap' }}>
                                            {pendingUserFacilities
                                                .sort((a, b) => (a.facName > b.facName ? 1 : -1))
                                                .map((facility, index) => {
                                                    const { currentTemplate, displayedRoles } = getFacilityRoleDisplay(facility);
                                                    return (
                                                        <tr key={index}>
                                                            <td style={{ verticalAlign: 'top', paddingTop: '10px' }}>{facility.facID}</td>
                                                            <td style={{ verticalAlign: 'top', paddingTop: '10px' }}>{facility.facName}</td>
                                                            <td style={{ verticalAlign: 'top', paddingTop: '10px', minWidth: '220px', whiteSpace: 'normal' }}>
                                                                <div style={{ width: '160px' }}>
                                                                    <Select
                                                                        className="react-select"
                                                                        classNamePrefix="react-select"
                                                                        hideSelectedOptions
                                                                        isSearchable={false}
                                                                        placeholder="Select template..."
                                                                        onChange={(selection) => {
                                                                            setHasPerformedAction(true);
                                                                            setSelectedRoleTemplates((prev) => ({ ...prev, [facility.facID]: selection }));
                                                                        }}
                                                                        value={currentTemplate}
                                                                        options={roleTemplates}
                                                                    />
                                                                </div>
                                                                <RoleBadgeList
                                                                    roles={displayedRoles}
                                                                    idPrefix={`user-form-pending-role-badges-${facility.facID}`}
                                                                    label=""
                                                                    editable
                                                                    availableRoles={facilityUserRoles || []}
                                                                    onAddRole={(roleValue) => addPendingFacilityRole(facility, displayedRoles, roleValue)}
                                                                    onRemoveRole={(roleValue) => removePendingFacilityRole(facility, displayedRoles, roleValue)}
                                                                />
                                                            </td>
                                                            <td style={{ verticalAlign: 'top', paddingTop: '10px' }}>
                                                                <Button className="card-drop p-0 text-white" onClick={() => deletePendingFacility(facility.facID)}>
                                                                    <Icon icon={deleteIcon} height="24" width="24" />
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </Table>
                                    {isKnownUser && (
                                        <>
                                            <hr />
                                            <div className="d-flex justify-content-end">
                                                <Button
                                                    color="primary"
                                                    disabled={pendingUserFacilities.some((f) => !getFacilityRoleDisplay(f).displayedRoles.length)}
                                                    onClick={handleSavePendingFacilities}>
                                                    Save Pending Facilities
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        )}

                        {isKnownUser && (
                            <>
                                <h4 className="ml-1">Facilities{userFacilities?.length ? ` (${userFacilities.length})` : ''}</h4>
                                {!readOnly && userFacilities?.length > 1 && (() => {
                                    const isBulkRoleEditingDisabled =
                                        userRoleLoading ||
                                        email === contextUser.email ||
                                        !(userFacilities || []).some((facility) =>
                                            checkIfUserIsAdmin(facilities.find((faci) => faci.facID === facility.facID))
                                        );
                                    return (
                                    <div className="d-flex" style={{ gap: '16px' }}>
                                        <FormGroup className="ml-1 mr-1" style={{ flex: 1 }}>
                                            <Label>Role Group (applies to all facilities)</Label>
                                            <Select
                                                className="react-select"
                                                classNamePrefix="react-select"
                                                hideSelectedOptions
                                                isSearchable={false}
                                                isClearable
                                                isDisabled={isBulkRoleEditingDisabled}
                                                placeholder="Select role group..."
                                                onChange={applyRoleGroupToAllFacilities}
                                                value={savedFacilitiesRoleGroup}
                                                options={roleTemplates}
                                            />
                                            <RoleBadgeList
                                                roles={getActiveRoleGroupRoles()}
                                                idPrefix="saved-facilities-role-group-badges"
                                                label=""
                                                maxVisible={4}
                                                editable={!isBulkRoleEditingDisabled}
                                                allowAdd={false}
                                                onRemoveRole={removeRoleFromAllFacilities}
                                            />
                                        </FormGroup>
                                        <FormGroup className="ml-1 mr-1" style={{ flex: 1 }}>
                                            <Label>Role (applies to all facilities)</Label>
                                            <Select
                                                className="react-select"
                                                classNamePrefix="react-select"
                                                isMulti
                                                hideSelectedOptions
                                                isSearchable={false}
                                                isClearable={false}
                                                backspaceRemovesValue={false}
                                                isDisabled={isBulkRoleEditingDisabled}
                                                placeholder="Select role..."
                                                components={{ ValueContainer: FacilityRoleValueContainer }}
                                                onChange={onRolesForAllFacilitiesChange}
                                                value={getRolesOnAllFacilities().map(
                                                    (role) => facilityUserRoles?.find((r) => r.value === role) ?? { value: role, label: role }
                                                )}
                                                options={(facilityUserRoles || []).filter(
                                                    (option) => !getActiveRoleGroupRoles().includes(option.value)
                                                )}
                                            />
                                        </FormGroup>
                                    </div>
                                    );
                                })()}
                                {userFacilities ? (
                                    <div
                                        style={{
                                            maxHeight: '320px',
                                            overflowY: 'auto',
                                            border: '1px solid #e9ecef',
                                            borderRadius: '4px',
                                        }}>
                                    <Table className="mb-0" size="sm">
                                        <thead>
                                            <tr>
                                                <th>Facility ID</th>
                                                <th>Facility Name</th>
                                                <th>Role</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody style={{ overflow: 'auto', whiteSpace: 'nowrap' }}>
                                            {userFacilities.length > 0 ? (
                                                userFacilities
                                                    .sort((a, b) => (a.facName > b.facName ? 1 : -1))
                                                    .map((facility, index) => {
                                                        const currentTemplate =
                                                            selectedRoleTemplates[facility.facID] ||
                                                            (facility.roleTemplate
                                                                ? {
                                                                      value: facility.roleTemplate.roleTemplateId,
                                                                      label: facility.roleTemplate.templateName,
                                                                      userRoles: facility.roleTemplate.userRoles || [],
                                                                  }
                                                                : null);
                                                        const pickedTemplate = selectedRoleTemplates[facility.facID];
                                                        const templateRoles = pickedTemplate
                                                            ? pickedTemplate.userRoles || []
                                                            : facility.templateRoles || [];
                                                        const combinedRoles = Array.from(
                                                            new Set([
                                                                ...templateRoles,
                                                                ...(facility.userRoles || []),
                                                                ...(facility.userRole ? [facility.userRole] : []),
                                                            ])
                                                        );
                                                        const displayedRoles = (facilityRoleOverrides[facility.facID] ?? combinedRoles).filter(
                                                            (r) => r !== selectedStandardRole?.value
                                                        );
                                                        const isRoleEditingDisabled =
                                                            readOnly ||
                                                            userRoleLoading ||
                                                            !checkIfUserIsAdmin(facilities.find((faci) => faci.facID === facility.facID)) ||
                                                            email === contextUser.email;
                                                        return (
                                                            <tr key={index}>
                                                                <td style={{ verticalAlign: 'top', paddingTop: '10px' }}>{facility.facID}</td>
                                                                <td style={{ verticalAlign: 'top', paddingTop: '10px' }}>{facility.facName}</td>
                                                                <td style={{ verticalAlign: 'top', paddingTop: '10px', minWidth: '220px', whiteSpace: 'normal' }}>
                                                                    <div style={{ width: '160px' }}>
                                                                        <Select
                                                                            className="react-select"
                                                                            classNamePrefix="react-select"
                                                                            hideSelectedOptions
                                                                            isSearchable={false}
                                                                            isDisabled={isRoleEditingDisabled}
                                                                            onChange={(selection) => {
                                                                                setHasPerformedAction(true);
                                                                                setSelectedRoleTemplates((prev) => ({ ...prev, [facility.facID]: selection }));
                                                                                onFaciRoleTemplateChange(selection.value, email, facility);
                                                                            }}
                                                                            value={currentTemplate}
                                                                            options={roleTemplates}
                                                                        />
                                                                    </div>
                                                                    <RoleBadgeList
                                                                        roles={displayedRoles}
                                                                        idPrefix={`user-form-role-badges-${facility.facID}`}
                                                                        label=""
                                                                        editable={!isRoleEditingDisabled}
                                                                        availableRoles={facilityUserRoles || []}
                                                                        onAddRole={(roleValue) => addFacilityRole(facility, displayedRoles, roleValue)}
                                                                        onRemoveRole={(roleValue) => removeFacilityRole(facility, displayedRoles, roleValue)}
                                                                    />
                                                                </td>
                                                                <td style={{ verticalAlign: 'top', paddingTop: '10px' }}>
                                                                    <Button
                                                                        color="link"
                                                                        className="card-drop p-0 text-muted"
                                                                        disabled={
                                                                            readOnly ||
                                                                            !checkIfUserIsAdmin(facilities.find((faci) => faci.facID === facility.facID)) ||
                                                                            email === contextUser.email
                                                                        }
                                                                        onClick={() => removeUserFromSingleFacility(facility.facID, facility.facName)}>
                                                                        <Icon icon={deleteIcon} height="20" width="20" />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} className="text-center">
                                                        <b>No User Facilities</b>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                    </div>
                                ) : (
                                    <div className="w-100 d-flex justify-content-center">
                                        <div>
                                            <h4>Loading User Facilities...</h4>
                                            <div className="bouncing-loader">
                                                <div></div>
                                                <div></div>
                                                <div></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    {readOnly ? 'Close' : 'Cancel'}
                </Button>
                {showDetailFields && !readOnly && (
                    <Button
                        color="primary"
                        disabled={saving || (isDetailsUnchanged && !pendingUserFacilities.length && !hasPerformedAction)}
                        onClick={handleSave}>
                        Save
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

const ResetPasswordModal = ({ isOpen, toggle, user }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    function handleValidSubmit() {
        if (password !== confirmPassword) return toast.error('Passwords do not match');

        let resetPasswordPayload = {
            password,
            confirmPassword,
            email: user.email,
            entityId: user.entityTypeAndRole[0].facid,
        };

        SrxApi.post('/adminpwdreset', resetPasswordPayload)
            .then((res) => {
                toast.success(res.data.message + ' for ' + '"' + res.data.email + '"');
                toggle();
            })
            .catch((err) => toast.error(err.message));
    }

    useEffect(() => {
        if (!isOpen) {
            setPassword('');
            setConfirmPassword('');
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle} className="modal-colored-header bg-primary">
                Reset Password for <i>"{user.displayName}"</i>
            </ModalHeader>
            <ModalBody>
                <AvForm onValidSubmit={() => handleValidSubmit()}>
                    <AvGroup>
                        <Label for="password">New Password</Label>
                        <AvField
                            type="password"
                            name="password"
                            id="password"
                            placeholder="Enter new password"
                            onChange={(e) => setPassword(e.target.value)}
                            value={password}
                            required
                            validate={{
                                minLength: {
                                    value: 4,
                                    errorMessage: 'Password must include a minimum of 4 characters',
                                },
                            }}
                        />
                    </AvGroup>

                    <AvGroup>
                        <Label for="confirm-password">Confirm New Password</Label>
                        <AvField
                            type="password"
                            name="confirm-password"
                            id="confirm-password"
                            placeholder="Confirm new password"
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            value={confirmPassword}
                            required
                            validate={{
                                minLength: {
                                    value: 4,
                                    errorMessage: 'Password must include a minimum of 4 characters',
                                },
                            }}
                        />
                        {/* <AvFeedback>This field is invalid</AvFeedback> */}
                    </AvGroup>

                    <FormGroup>
                        <Button color="secondary">Reset Password</Button>
                    </FormGroup>
                </AvForm>
            </ModalBody>
        </Modal>
    );
};

export default Users;
